import { css } from "@emotion/react";
import Link from "@tiptap/extension-link";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useState } from "react";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton, SecondaryButton } from "../../bits/buttons";
import { Input } from "../../bits/inputs";
import { LoadingSpinner } from "../../bits/loading";
import { Modal } from "../../bits/Modal";
import { useTitle } from "../../hooks/useTitle";
import defaultContent from "../../utils/loginPageAdminDefault";
import { useConfig } from "../../hooks/useConfig";

const toolbarButtonCss = css`
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #f0f0f0;
  }
  &.is-active {
    background: #e0e0e0;
    border-color: #999;
    font-weight: bold;
  }
`;

export const LoginPageAdmin = () => {
  useTitle("Login Page Admin | Compass");

  const { loginPageContent } = useConfig();
  const updateSiteSettings = trpc.adminUpdateSiteSettings.useMutation();

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkUrlError, setLinkUrlError] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer" },
      }),
    ],
    content: loginPageContent || defaultContent,
    editorProps: {
      attributes: {
        style:
          "min-height: 200px; outline: none; font-family: inherit; font-size: 15px; line-height: 1.6;",
      },
    },
  });
  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor.isActive("bold"),
      isItalic: ctx.editor.isActive("italic"),
      isH1: ctx.editor.isActive("heading", { level: 1 }),
      isH2: ctx.editor.isActive("heading", { level: 2 }),
      isH3: ctx.editor.isActive("heading", { level: 3 }),
      isBulletList: ctx.editor.isActive("bulletList"),
      isOrderedList: ctx.editor.isActive("orderedList"),
      isLink: ctx.editor.isActive("link"),
    }),
  });

  const handleSave = () => {
    if (!editor) return;
    updateSiteSettings.mutate({ loginPageContent: editor.getHTML() });
  };

  const isSafeUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return ["https:", "mailto:"].includes(parsed.protocol);
    } catch {
      // Allow relative paths (e.g. /page or #anchor)
      return url.startsWith("/") || url.startsWith("#");
    }
  };

  const openLinkModal = () => {
    if (!editor) return;
    setLinkUrl((editor.getAttributes("link").href as string) ?? "");
    setLinkUrlError("");
    setIsLinkModalOpen(true);
  };

  const confirmLink = () => {
    if (!editor) return;
    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      if (!isSafeUrl(linkUrl)) {
        setLinkUrlError(
          "Only https://, http://, and mailto: URLs are allowed.",
        );
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl })
        .run();
    }
    setIsLinkModalOpen(false);
  };

  if (!editor) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ padding: "1rem", maxWidth: 800 }}>
      <h2 style={{ marginBottom: "1rem" }}>Login Page Content</h2>
      <p
        css={css`
          color: #666;
          margin-bottom: 1.5rem;
          font-size: 14px;
        `}
      >
        Edit the marketing content shown on the left side of the login screen.
      </p>

      <div
        css={css`
          border: 1px solid #ccc;
          border-radius: 8px;
          overflow: hidden;
        `}
      >
        {/* Toolbar */}
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            padding: 8px;
            border-bottom: 1px solid #ccc;
            background: #fafafa;
          `}
        >
          <button
            css={toolbarButtonCss}
            className={editorState?.isBold ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </button>
          <button
            css={toolbarButtonCss}
            className={editorState?.isItalic ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </button>
          <div
            css={css`
              width: 1px;
              background: #ddd;
              margin: 0 4px;
            `}
          />
          <button
            css={toolbarButtonCss}
            className={editorState?.isH1 ? "is-active" : ""}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            H1
          </button>
          <button
            css={toolbarButtonCss}
            className={editorState?.isH2 ? "is-active" : ""}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            H2
          </button>
          <button
            css={toolbarButtonCss}
            className={editorState?.isH3 ? "is-active" : ""}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            H3
          </button>
          <div
            css={css`
              width: 1px;
              background: #ddd;
              margin: 0 4px;
            `}
          />
          <button
            css={toolbarButtonCss}
            className={editorState?.isBulletList ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            • List
          </button>
          <button
            css={toolbarButtonCss}
            className={editorState?.isOrderedList ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1. List
          </button>
          <div
            css={css`
              width: 1px;
              background: #ddd;
              margin: 0 4px;
            `}
          />
          <button
            css={toolbarButtonCss}
            className={editorState?.isLink ? "is-active" : ""}
            onClick={openLinkModal}
          >
            Link
          </button>
          {editorState?.isLink && (
            <button
              css={toolbarButtonCss}
              onClick={() => editor.chain().focus().unsetLink().run()}
            >
              Unlink
            </button>
          )}
        </div>

        {/* Editor content */}
        <div
          css={css`
            padding: 16px;
            background: white;

            .tiptap {
              h1 {
                font-size: 1.8em;
                font-weight: bold;
                margin: 0.5em 0;
              }
              h2 {
                font-size: 1.4em;
                font-weight: bold;
                margin: 0.5em 0;
              }
              h3 {
                font-size: 1.2em;
                font-weight: bold;
                margin: 0.5em 0;
              }
              p {
                margin: 0.5em 0;
              }
              ul {
                padding-left: 1.5em;
                list-style-type: disc;
              }
              ol {
                padding-left: 1.5em;
                list-style-type: decimal;
              }
              li {
                margin: 0.25em 0;
              }
              a {
                color: #0052cd;
                text-decoration: underline;
              }
            }
          `}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 1rem;
        `}
      >
        <PrimaryButton
          onClick={handleSave}
          disabled={updateSiteSettings.isLoading}
        >
          {updateSiteSettings.isLoading ? "Saving…" : "Save"}
        </PrimaryButton>
        {updateSiteSettings.isSuccess && (
          <span
            css={css`
              color: green;
              font-size: 14px;
            `}
          >
            Saved
          </span>
        )}
        {updateSiteSettings.error && (
          <span
            css={css`
              color: red;
              font-size: 14px;
            `}
          >
            {updateSiteSettings.error.message}
          </span>
        )}
      </div>

      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        renderButton={() => null}
        header={<h3>Insert Link</h3>}
        content={
          <>
            <Input
              label="URL"
              value={linkUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLinkUrl(e.target.value);
                setLinkUrlError("");
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") confirmLink();
              }}
              placeholder="https://"
              fullWidth
              autoFocus
            />
            {linkUrlError && (
              <p
                css={css`
                  color: red;
                  font-size: 13px;
                  margin-top: 4px;
                `}
              >
                {linkUrlError}
              </p>
            )}
          </>
        }
        footer={
          <>
            <SecondaryButton onClick={() => setIsLinkModalOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={confirmLink}>Apply</PrimaryButton>
          </>
        }
      />
    </div>
  );
};
